/**
 * Intercepteur global pour fetch()
 *
 * Gère automatiquement :
 * - Le refresh du token JWT (header X-New-Token)
 * - La capture des erreurs réseau/CORS pour le module erreurs
 * - La capture des erreurs HTTP (4xx/5xx) pour le module erreurs
 * - La synchronisation du token entre onglets
 * - La vérification proactive de l'expiration du token
 *
 * IMPORTANT: Ce fichier doit être importé AVANT tout appel fetch()
 */

import { API_ENDPOINTS } from './apiConfig';

const originalFetch = window.fetch;

// 🔧 MODE DEBUG - synchronisé avec logger.setAuthDebug(true/false)
// Pour activer : localStorage.setItem('debug_auth', 'true') ou logger.setAuthDebug(true)
const isDebugEnabled = () => {
  try {
    return localStorage.getItem('debug_auth') === 'true';
  } catch {
    return false;
  }
};

// 🔧 TRAP localStorage.setItem pour tracer QUI écrit le token (activé par isDebugEnabled())
if (isDebugEnabled()) {
  const originalSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = function(key, value) {
    if (key === 'token') {
      let tokenInfo = 'invalid';
      try {
        const payload = JSON.parse(new TextDecoder().decode(
          Uint8Array.from(atob(value.split('.')[1]), c => c.charCodeAt(0))
        ));
        tokenInfo = `iat=${payload.iat} (${new Date(payload.iat * 1000).toISOString()}), exp=${payload.exp} (${new Date(payload.exp * 1000).toISOString()})`;
      } catch (e) { /* ignore */ }

      console.warn('🔴 [DEBUG JWT] localStorage.setItem("token") APPELÉ!', {
        tokenInfo,
        tokenPreview: value?.substring(0, 50) + '...',
        stack: new Error().stack
      });
    }
    return originalSetItem(key, value);
  };
}

/**
 * Décode le payload JWT avec support UTF-8 (pour les noms avec accents)
 */
const decodeJWTPayload = (token) => {
  const base64 = token.split('.')[1];
  const binaryString = atob(base64);
  // Convertir en Uint8Array pour gérer correctement l'UTF-8
  const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
  const decoded = new TextDecoder().decode(bytes);
  const payload = JSON.parse(decoded);

  if (isDebugEnabled()) {
    console.log('🔍 [DEBUG JWT] Payload décodé:', {
      userId: payload.userId,
      email: payload.email,
      name: `${payload.first_name} ${payload.last_name}`,
      iat: payload.iat,
      exp: payload.exp,
      iatDate: new Date(payload.iat * 1000).toISOString(),
      expDate: new Date(payload.exp * 1000).toISOString()
    });
  }

  return payload;
};

/**
 * Vérifie si un token JWT est expiré (avec marge de sécurité de 30 secondes)
 */
const isTokenExpired = (token) => {
  if (!token) {
    if (isDebugEnabled()) console.log('🔍 [DEBUG JWT] isTokenExpired: pas de token');
    return true;
  }
  try {
    const payload = decodeJWTPayload(token);
    const now = Date.now();
    const expMs = payload.exp * 1000;
    const isExpired = expMs < (now + 30000);

    if (isDebugEnabled()) {
      console.log('🔍 [DEBUG JWT] Vérification expiration:', {
        expMs,
        nowMs: now,
        nowPlus30s: now + 30000,
        expDate: new Date(expMs).toISOString(),
        nowDate: new Date(now).toISOString(),
        isExpired,
        secondsUntilExpiry: Math.round((expMs - now) / 1000)
      });
    }

    return isExpired;
  } catch (error) {
    if (isDebugEnabled()) console.error('🔍 [DEBUG JWT] Erreur décodage:', error);
    return true;
  }
};

/**
 * Synchronisation du token entre onglets via localStorage
 * Quand un autre onglet met à jour le token, on synchronise
 */
window.addEventListener('storage', (e) => {
  if (e.key === 'token') {
    if (isDebugEnabled()) {
      // Décoder les tokens pour voir les dates
      let oldTokenInfo = 'null';
      let newTokenInfo = 'null';
      try {
        if (e.oldValue) {
          const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(e.oldValue.split('.')[1]), c => c.charCodeAt(0))));
          oldTokenInfo = `exp=${new Date(payload.exp * 1000).toISOString()}`;
        }
        if (e.newValue) {
          const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(e.newValue.split('.')[1]), c => c.charCodeAt(0))));
          newTokenInfo = `exp=${new Date(payload.exp * 1000).toISOString()}`;
        }
      } catch (err) { /* ignore */ }

      console.warn('🔄 [DEBUG JWT] Storage event - TOKEN MODIFIÉ PAR AUTRE ONGLET!', {
        oldValue: e.oldValue?.substring(0, 30) + '...',
        newValue: e.newValue?.substring(0, 30) + '...',
        oldTokenInfo,
        newTokenInfo,
        timestamp: new Date().toISOString()
      });
    }

    if (e.newValue) {
      // PROTECTION: Vérifier si le nouveau token est plus récent que l'ancien
      // Si un autre onglet essaie d'écraser avec un vieux token, on restaure l'ancien (plus récent)
      if (e.oldValue) {
        try {
          const oldPayload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(e.oldValue.split('.')[1]), c => c.charCodeAt(0))));
          const newPayload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(e.newValue.split('.')[1]), c => c.charCodeAt(0))));

          if (newPayload.exp < oldPayload.exp) {
            // Le nouveau token est PLUS VIEUX que l'ancien - on le refuse et on restaure
            console.warn('🛡️ [DEBUG JWT] BLOQUÉ: Tentative d\'écraser un token récent par un ancien!', {
              oldTokenExp: new Date(oldPayload.exp * 1000).toISOString(),
              newTokenExp: new Date(newPayload.exp * 1000).toISOString(),
              action: 'Restauration du token récent'
            });
            // Restaurer l'ancien token (plus récent)
            localStorage.setItem('token', e.oldValue);
            return; // Ne pas propager l'événement
          }
        } catch (err) {
          // En cas d'erreur de décodage, on laisse passer
          if (isDebugEnabled()) console.warn('🔍 [DEBUG JWT] Erreur comparaison tokens:', err.message);
        }
      }

      // Un autre onglet a mis à jour le token (login ou refresh)
      console.info('🔄 Token synchronisé depuis un autre onglet');
      window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
        detail: { token: e.newValue, source: 'cross-tab' }
      }));
    } else {
      // Un autre onglet a supprimé le token (logout ou 401)
      console.info('🔒 Déconnexion détectée dans un autre onglet');
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { reason: 'cross_tab_logout' }
      }));
    }
  }
});

// URLs à exclure du reporting d'erreurs (éviter boucles infinies)
const EXCLUDED_URLS = [
  '/api/perf/client-error',
  '/api/analytics/track'
];

// URLs où un 401 est attendu et ne doit pas déclencher de redirection
const AUTH_URLS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password'
];

// Anti-spam : éviter de déclencher plusieurs redirections 401 de suite
let lastUnauthorizedTime = 0;
const UNAUTHORIZED_DEBOUNCE_MS = 5000;

// Anti-spam : ne reporter qu'UNE erreur 401 par session (évite de polluer les logs)
let hasReported401 = false;

// Patterns d'erreurs liées à l'authentification à ne pas reporter (bruit)
const AUTH_ERROR_PATTERNS = [
  /token.*invalide/i,
  /token.*expir/i,
  /jwt.*expired/i,
  /jwt.*invalid/i,
  /session.*expir/i,
  /unauthorized/i,
  /authentification/i
];

/**
 * Vérifie si une erreur est liée à l'authentification (token/session)
 */
const isAuthError = (errorData) => {
  const message = errorData.error?.message || '';
  return AUTH_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

/**
 * Envoie une erreur au backend pour logging
 * Utilise originalFetch pour éviter la récursion
 *
 * IMPORTANT: L'endpoint /api/perf/client-error est PUBLIC pour pouvoir
 * capturer les erreurs 401 (token expiré/invalide) qui ne pourraient pas
 * être reportées si l'authentification était requise.
 */
const reportError = async (url, errorData) => {
  // Ne pas reporter les erreurs de ces URLs
  if (EXCLUDED_URLS.some(excluded => url.includes(excluded))) {
    return;
  }

  // Ne pas reporter les erreurs d'auth/token (très fréquentes, polluent les logs)
  // Ces erreurs sont normales et gérées par le refresh token automatique
  if (isAuthError(errorData)) {
    if (isDebugEnabled()) {
      console.log('[fetchInterceptor] Erreur auth ignorée:', errorData.error?.message);
    }
    return;
  }

  // Ne reporter qu'UNE SEULE erreur 401 par session (évite le spam de logs)
  if (errorData.error?.status === 401 || errorData.error?.type === 'session_expired') {
    if (hasReported401) {
      return; // Déjà reporté, on ignore
    }
    hasReported401 = true;
  }

  // Token optionnel - l'endpoint est public mais on envoie le token si disponible
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    await originalFetch(API_ENDPOINTS.PERF_CLIENT_ERROR, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        error: errorData.error,
        url: window.location.href,
        userAgent: navigator.userAgent,
        metadata: {
          requestUrl: url,
          ...errorData.metadata,
          timestamp: new Date().toISOString()
        }
      })
    });
  } catch (e) {
    // Silently fail - don't create infinite loop
  }
};

/**
 * Intercepte les réponses pour gérer le refresh token automatique
 * et capturer les erreurs réseau ET HTTP
 *
 * IMPORTANT: Remplace automatiquement le token Authorization par celui
 * de localStorage pour garantir d'utiliser toujours le token le plus récent
 */
const interceptedFetch = async (...args) => {
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || 'unknown';
  const isAuthUrl = AUTH_URLS.some(authUrl => url.includes(authUrl));

  // Remplacer le token Authorization par celui de localStorage (le plus récent)
  const currentToken = localStorage.getItem('token');

  // VÉRIFICATION PROACTIVE : Si le token est expiré, déclencher la déconnexion immédiatement
  // Sauf pour les routes d'authentification (login, reset password, etc.)
  if (currentToken && !isAuthUrl) {
    const tokenExpired = isTokenExpired(currentToken);
    if (isDebugEnabled()) {
      console.log('🔍 [DEBUG JWT] Vérification avant requête:', {
        url,
        isAuthUrl,
        hasToken: !!currentToken,
        tokenExpired,
        tokenPreview: currentToken?.substring(0, 50) + '...'
      });
    }
    if (tokenExpired) {
      const now = Date.now();
      if (now - lastUnauthorizedTime >= UNAUTHORIZED_DEBOUNCE_MS) {
        lastUnauthorizedTime = now;
        console.warn('🔒 [DEBUG JWT] Token expiré détecté AVANT requête!', {
          url,
          dispatchingUnauthorized: true,
          timestamp: new Date().toISOString()
        });
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { reason: 'token_expired_proactive', url }
        }));
      }
      // Retourner une fausse réponse 401 SANS la reporter (évite le spam d'erreurs)
      const response = new Response(JSON.stringify({ error: 'Token expiré' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' }
      });
      response._proactiveExpiration = true; // Flag pour éviter le reporting
      return response;
    }
  }

  // IMPORTANT: Cloner les options pour éviter les problèmes d'immutabilité
  // et FORCER le remplacement du token Authorization
  if (args[1]) {
    const originalHeaders = args[1].headers || {};
    const hasAuth = originalHeaders['Authorization'] || originalHeaders['authorization'];

    if (hasAuth) {
      const newHeaders = { ...originalHeaders };

      if (currentToken) {
        // Token valide : le remplacer par celui de localStorage
        if (originalHeaders['Authorization']) {
          newHeaders['Authorization'] = `Bearer ${currentToken}`;
        }
        if (originalHeaders['authorization']) {
          newHeaders['authorization'] = `Bearer ${currentToken}`;
        }
      } else if (!isAuthUrl) {
        // PAS de token et route protégée : supprimer le header Authorization
        // pour éviter d'envoyer un header vide/malformé
        delete newHeaders['Authorization'];
        delete newHeaders['authorization'];

        // Déclencher la déconnexion si pas déjà fait
        const now = Date.now();
        if (now - lastUnauthorizedTime >= UNAUTHORIZED_DEBOUNCE_MS) {
          lastUnauthorizedTime = now;
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: { reason: 'no_token', url }
          }));
        }
        // Retourner une fausse réponse sans reporter
        const response = new Response(JSON.stringify({ error: 'Non authentifié' }), {
          status: 401,
          headers: { 'Content-Type': 'application/json' }
        });
        response._proactiveExpiration = true;
        return response;
      }

      // Remplacer args[1] par un nouvel objet avec les nouveaux headers
      args[1] = { ...args[1], headers: newHeaders };
    }
  }

  try {
    const response = await originalFetch(...args);

    // Vérifier si le serveur a renvoyé un nouveau token
    const newToken = response.headers.get('X-New-Token');

    if (newToken) {
      // PROTECTION: Vérifier que le nouveau token est plus récent que l'actuel
      const currentToken = localStorage.getItem('token');
      let shouldUpdate = true;

      if (currentToken) {
        try {
          const currentPayload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(currentToken.split('.')[1]), c => c.charCodeAt(0))));
          const newPayload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(newToken.split('.')[1]), c => c.charCodeAt(0))));

          if (newPayload.exp <= currentPayload.exp) {
            // Le nouveau token n'est PAS plus récent - on l'ignore
            if (isDebugEnabled()) {
              console.warn('🛡️ [DEBUG JWT] X-New-Token ignoré (pas plus récent):', {
                currentExp: new Date(currentPayload.exp * 1000).toISOString(),
                newExp: new Date(newPayload.exp * 1000).toISOString(),
                url
              });
            }
            shouldUpdate = false;
          }
        } catch (err) {
          // En cas d'erreur de décodage, on accepte le nouveau token
          if (isDebugEnabled()) console.warn('🔍 [DEBUG JWT] Erreur comparaison X-New-Token:', err.message);
        }
      }

      if (shouldUpdate) {
        localStorage.setItem('token', newToken);
        hasReported401 = false; // Réinitialiser le flag de report 401
        // Émettre un événement pour que App.js puisse mettre à jour son state React
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
          detail: { token: newToken }
        }));
      }
    }

    // GESTION DES 401 : Rediriger vers login si le token est expiré/invalide
    // Sauf pour les routes d'authentification où un 401 est attendu
    if (response.status === 401 && !AUTH_URLS.some(authUrl => url.includes(authUrl))) {
      const now = Date.now();

      // Anti-spam : éviter les redirections multiples
      if (now - lastUnauthorizedTime >= UNAUTHORIZED_DEBOUNCE_MS) {
        lastUnauthorizedTime = now;

        // Logger l'événement
        console.warn('🔒 Session expirée (401), redirection vers login', {
          url,
          timestamp: new Date().toISOString()
        });

        // Reporter au backend (fire & forget)
        reportError(url, {
          error: {
            type: 'session_expired',
            message: 'Token expiré - redirection automatique vers login',
            name: 'SESSION_EXPIRED',
            status: 401
          },
          metadata: {
            errorType: 'session_expired',
            httpStatus: 401,
            autoRedirect: true
          }
        });

        // Nettoyer le token invalide
        localStorage.removeItem('token');

        // Émettre l'événement pour que App.js redirige vers login
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { reason: 'token_expired', url }
        }));
      }
    }

    // Capturer les erreurs HTTP (4xx, 5xx) - sauf pour les URLs exclues
    if (!response.ok && !EXCLUDED_URLS.some(excluded => url.includes(excluded))) {
      // Essayer de lire le corps de la réponse pour plus de détails
      let errorDetails = '';
      try {
        const clonedResponse = response.clone();
        const data = await clonedResponse.json();
        errorDetails = data.error || data.message || JSON.stringify(data);
      } catch (e) {
        // Impossible de parser le JSON, utiliser le statusText
        errorDetails = response.statusText;
      }

      reportError(url, {
        error: {
          type: 'http_error',
          message: `HTTP ${response.status}: ${errorDetails}`,
          name: `HTTP_${response.status}`,
          status: response.status
        },
        metadata: {
          errorType: 'http',
          httpStatus: response.status,
          httpStatusText: response.statusText,
          errorDetails
        }
      });
    }

    return response;
  } catch (error) {
    // Erreur réseau (CORS, offline, DNS, etc.)
    reportError(url, {
      error: {
        type: 'network_error',
        message: error.message || 'Erreur réseau',
        name: error.name
      },
      metadata: {
        errorType: error.name === 'TypeError' && error.message.includes('CORS') ? 'cors' : 'network'
      }
    });
    throw error; // Re-throw pour que l'appelant puisse gérer
  }
};

// Remplacer fetch globalement
window.fetch = interceptedFetch;

// Exporter pour les tests et pour App.js (debug)
export { originalFetch, interceptedFetch, isDebugEnabled };
